import React, { useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { SavedViewsCarousel } from './reusablePartials';
import { AppProvider } from './context/AppState.jsx';
import { ModalProvider } from './context/modalContext.jsx';
import useAppState from './hooks/useAppState.jsx';
import { getCarouselContent } from './actions';

// Function to mount React carousel as a standalone React root
function mountReactCarousel() {
  // Check if React carousel already exists
  if (document.getElementById('react-carousel-root')) {
    return;
  }

  // Find the saved-slider-wrapper element from the template
  const carouselWrapper = document.querySelector('.saved-slider-wrapper');
  if (!carouselWrapper) {
    // Carousel wrapper doesn't exist on this page - that's fine, not all pages have it
    return;
  }

  // Create a container for the React carousel
  const carouselContainer = document.createElement('div');
  carouselContainer.id = 'react-carousel-root';

  // Replace the template content with React carousel
  carouselWrapper.innerHTML = '';
  carouselWrapper.appendChild(carouselContainer);

  // Verify React is available
  if (typeof React === 'undefined' || typeof createRoot === 'undefined') {
    console.error('React or createRoot not available for carousel mount');
    return;
  }

  // Create the carousel app component with context
  const CarouselApp = () => {
    const [state, dispatch] = useAppState();

    // Load carousel content directly
    useEffect(() => {
      if (!state.carouselContent) {
        getCarouselContent()
          .then((carouselContent) => {
            dispatch({
              type: 'carouselContent',
              payload: carouselContent,
            });
          })
          .catch((err) => {
            console.error('Error loading carousel content:', err);
          });
      }
    }, [state.carouselContent, dispatch]);

    return (
      <AppProvider value={[state, dispatch]}>
        <ModalProvider>
          <SavedViewsCarousel />
        </ModalProvider>
      </AppProvider>
    );
  };

  // Mount the React carousel using createRoot
  try {
    const root = createRoot(carouselContainer);
    root.render(<CarouselApp />);
  } catch (error) {
    console.error('Error mounting React carousel:', error);
    // Fallback: show a message if mount fails
    carouselContainer.innerHTML = '<p>Carousel loading...</p>';
  }
}

// Mount carousel when DOM is ready
// Use window load to ensure all scripts including React are loaded
if (document.readyState === 'loading') {
  window.addEventListener('load', () => {
    // Small delay to ensure React and other dependencies are fully initialized
    setTimeout(mountReactCarousel, 100);
  });
} else {
  // If already loaded, wait a bit for React to be ready
  setTimeout(mountReactCarousel, 200);
}
